/**
 * ConsoleBottomPanel
 *
 * A fixed bottom-panel console that streams live Mycelium/Cortex events.
 *
 * - Toggle with Cmd+` (Mac) / Ctrl+` (Win/Linux) or the shared ConsoleToggle button.
 * - Open state is persisted in localStorage.
 * - Click an event row to expand its detail inline.
 * - Accepts events as props; the host app is responsible for the hook.
 *
 * @module ConsoleBottomPanel
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  subscribeOrchestrationConsole,
  toggleOrchestrationConsole,
  type ConsoleFocusDetail,
} from "../console-events";
import type { CortexEventLogRow } from "@flowstate/types";
import { cn } from "../lib/cn";
import { toConsoleTab } from "../lib/domain";
import { ConnectionIndicator } from "./ConnectionIndicator";
import { ConsoleTabs, type ConsoleTab } from "./ConsoleTabs";
import { DevModeToggle, useDevMode } from "./DevModeToggle";
import { EmptyState } from "./EmptyState";
import { EventDetail } from "./EventDetail";
import { EventRow } from "./EventRow";
import { X, Trash2, Terminal, Download, GripHorizontal } from "lucide-react";

// ── Layout helpers ─────────────────────────────────────────────────────────

/**
 * Measure the left admin sidebar's width so the console can snap to its right
 * edge. Returns 0 when no sidebar is present (e.g. landing/login, focus mode).
 */
type CleanupFn = () => void;

function observeElement(
  selector: () => HTMLElement | null,
  onAttach: (el: HTMLElement) => CleanupFn,
): CleanupFn {
  let currentCleanup: CleanupFn | null = null;
  let mounted = true;

  const cleanupCurrent = () => {
    currentCleanup?.();
    currentCleanup = null;
  };

  const attach = () => {
    if (!mounted) return;
    const el = selector();
    if (el) {
      cleanupCurrent();
      currentCleanup = onAttach(el);
    }
  };

  attach();

  const bodyObserver = new MutationObserver(attach);
  bodyObserver.observe(document.body, { childList: true, subtree: true });

  return () => {
    mounted = false;
    cleanupCurrent();
    bodyObserver.disconnect();
  };
}

/**
 * Measure the left admin sidebar's width so the console can snap to its right
 * edge. Returns 0 when no sidebar is present (e.g. landing/login, focus mode).
 */
function useLeftSidebarWidth(): number {
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (typeof window === "undefined") return;

    return observeElement(
      () => {
        const sidebar = document.querySelector(
          '[data-side="left"]:not([data-mobile="true"])',
        ) as HTMLElement | null;
        return sidebar?.querySelector(".fixed") as HTMLElement | null;
      },
      (fixedSidebar) => {
        const update = () => {
          const rect = fixedSidebar.getBoundingClientRect();
          setWidth(rect.width);
        };
        update();
        const observer = new ResizeObserver(update);
        observer.observe(fixedSidebar);
        return () => observer.disconnect();
      },
    );
  }, []);

  return width;
}

/**
 * Measure the right Cortex drawer occupancy so the console can snap its right
 * edge to the drawer's left edge. Mirrors useLeftSidebarWidth: no CSS transition
 * on the console — ResizeObserver tracks the drawer shell as its layout width
 * animates open/closed.
 */
function useRightDrawerWidth(): number {
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (typeof window === "undefined") return;

    return observeElement(
      () => document.querySelector('[data-cortex-drawer="right"]') as HTMLElement | null,
      (drawer) => {
        const update = () => {
          const isFullscreen = drawer.getAttribute("data-fullscreen") === "true";
          if (isFullscreen) {
            setWidth(0);
            return;
          }
          const rect = drawer.getBoundingClientRect();
          const occupied = Math.max(
            0,
            Math.min(rect.width, window.innerWidth - rect.left),
          );
          setWidth(occupied);
        };
        update();

        const resizeObserver = new ResizeObserver(update);
        resizeObserver.observe(drawer);

        const mutationObserver = new MutationObserver(update);
        mutationObserver.observe(drawer, {
          attributes: true,
          attributeFilter: ["data-fullscreen"],
        });

        return () => {
          resizeObserver.disconnect();
          mutationObserver.disconnect();
        };
      },
    );
  }, []);

  return width;
}

// ── Constants ──────────────────────────────────────────────────────────────

const OPEN_KEY = "flowstate:console:open";
const HEIGHT_KEY = "flowstate:console:bottom-height";
const DEFAULT_HEIGHT = 320;
const MIN_HEIGHT = 160;
const MAX_HEIGHT = 720;

function readStoredOpen(): boolean {
  try {
    return localStorage.getItem(OPEN_KEY) === "true";
  } catch {
    return false;
  }
}

function writeStoredOpen(open: boolean): void {
  try {
    localStorage.setItem(OPEN_KEY, String(open));
  } catch {
    /* ignore */
  }
}

function readStoredHeight(): number {
  try {
    const raw = localStorage.getItem(HEIGHT_KEY);
    const parsed = raw ? parseInt(raw, 10) : NaN;
    return Number.isFinite(parsed) && parsed >= MIN_HEIGHT && parsed <= MAX_HEIGHT
      ? parsed
      : DEFAULT_HEIGHT;
  } catch {
    return DEFAULT_HEIGHT;
  }
}

function writeStoredHeight(height: number): void {
  try {
    localStorage.setItem(HEIGHT_KEY, String(height));
  } catch {
    /* ignore */
  }
}

// ── Props ──────────────────────────────────────────────────────────────────

export interface ConsoleBottomPanelProps {
  /** Live event list from useOrchestrationEvents. */
  events: CortexEventLogRow[];
  isLoading?: boolean;
  isConnected?: boolean;
  /** Called when the user presses the clear button. */
  onClear?: () => void;
  /**
   * Optional export callback. When provided, an Export button appears in the
   * header. Called with the current (possibly filtered) events list.
   */
  onExport?: (events: CortexEventLogRow[]) => void;
  /**
   * If provided, the panel opens automatically when this changes to a
   * non-null value (useful in the Cortex surface: open on session start).
   */
  autoOpenOnSessionId?: string | null;
  /** Extra classname for the panel root. */
  className?: string;
  /** Hide the floating trigger (e.g. while Cortex chat is open). */
  hideFloatingTrigger?: boolean;
}

// ── Component ──────────────────────────────────────────────────────────────

export function ConsoleBottomPanel({
  events,
  isLoading = false,
  isConnected = false,
  onClear,
  onExport,
  autoOpenOnSessionId,
  className,
  hideFloatingTrigger = false,
}: ConsoleBottomPanelProps) {
  const [isOpen, setIsOpen] = useState(readStoredOpen);
  const [height, setHeight] = useState(readStoredHeight);
  const [activeTab, setActiveTab] = useState<ConsoleTab>("all");
  const [selectedEvent, setSelectedEvent] = useState<CortexEventLogRow | null>(null);
  const [focusFilter, setFocusFilter] = useState<ConsoleFocusDetail | null>(null);
  const [devMode] = useDevMode();
  const sidebarWidth = useLeftSidebarWidth();
  const rightDrawerWidth = useRightDrawerWidth();
  const listRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);
  const startYRef = useRef(0);
  const startHeightRef = useRef(0);

  const toggle = useCallback(() => {
    setIsOpen((prev) => {
      writeStoredOpen(!prev);
      return !prev;
    });
  }, []);

  // Keyboard shortcut: Cmd+` or Ctrl+`
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "`") {
        e.preventDefault();
        toggle();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [toggle]);

  // Auto-open when session changes
  const prevSessionRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (
      autoOpenOnSessionId &&
      autoOpenOnSessionId !== prevSessionRef.current
    ) {
      prevSessionRef.current = autoOpenOnSessionId;
      setIsOpen(true);
      writeStoredOpen(true);
    }
  }, [autoOpenOnSessionId]);

  // Open/toggle/focus from host apps via console-events
  const openPanel = useCallback(() => {
    setIsOpen(true);
    writeStoredOpen(true);
  }, []);

  const applyFocus = useCallback(
    (detail: ConsoleFocusDetail) => {
      setFocusFilter(detail);
      setSelectedEvent(null);
      if (detail.domain) {
        const tab = toConsoleTab(detail.domain);
        if (tab) setActiveTab(tab);
        else setActiveTab("all");
      }
      openPanel();
    },
    [openPanel],
  );

  useEffect(() => {
    return subscribeOrchestrationConsole({
      onToggle: toggle,
      onOpen: openPanel,
      onFocus: applyFocus,
    });
  }, [toggle, openPanel, applyFocus]);

  // Reset selected event when tab changes
  useEffect(() => {
    setSelectedEvent(null);
  }, [activeTab]);

  // Filter events by active tab (top-level domain) then optional mesh focus
  const filteredEvents = useMemo<CortexEventLogRow[]>(() => {
    let list =
      activeTab === "all"
        ? events
        : events.filter((e) => toConsoleTab(e.domain) === activeTab);

    if (!focusFilter) return list;

    if (focusFilter.runId) {
      list = list.filter(
        (e) =>
          e.cortex_run_id === focusFilter.runId ||
          e.event_id === focusFilter.runId ||
          e.correlation_id === focusFilter.runId,
      );
    }

    const terms = (focusFilter.terms ?? [])
      .map((t) => t.trim().toLowerCase())
      .filter(Boolean);
    if (terms.length === 0) return list;

    return list.filter((e) => {
      const hay = [
        e.event_type,
        e.summary,
        e.domain,
        e.source,
        typeof e.payload?.specialist_type === "string"
          ? e.payload.specialist_type
          : "",
        typeof e.payload?.status === "string" ? e.payload.status : "",
      ]
        .join(" ")
        .toLowerCase();
      return terms.some((t) => hay.includes(t));
    });
  }, [events, activeTab, focusFilter]);

  // Count per tab
  const counts = useMemo(() => {
    const c: Partial<Record<ConsoleTab, number>> = {
      all: events.length,
    };
    for (const e of events) {
      const d = toConsoleTab(e.domain);
      if (d) {
        c[d] = (c[d] ?? 0) + 1;
      }
    }
    return c;
  }, [events]);

  // Resize handlers
  const handleResizeStart = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      isDraggingRef.current = true;
      startYRef.current = "touches" in e ? e.touches[0].clientY : e.clientY;
      startHeightRef.current = height;
      document.body.style.cursor = "ns-resize";
      document.body.style.userSelect = "none";
    },
    [height],
  );

  useEffect(() => {
    const handleMove = (e: MouseEvent | TouchEvent) => {
      if (!isDraggingRef.current) return;
      const clientY =
        "touches" in e && (e as TouchEvent).touches.length > 0
          ? (e as TouchEvent).touches[0].clientY
          : (e as MouseEvent).clientY;
      const delta = startYRef.current - clientY;
      const nextHeight = Math.max(
        MIN_HEIGHT,
        Math.min(MAX_HEIGHT, startHeightRef.current + delta),
      );
      setHeight(nextHeight);
    };

    const handleUp = () => {
      if (!isDraggingRef.current) return;
      isDraggingRef.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      writeStoredHeight(height);
    };

    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    window.addEventListener("touchmove", handleMove);
    window.addEventListener("touchend", handleUp);

    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
      window.removeEventListener("touchmove", handleMove);
      window.removeEventListener("touchend", handleUp);
    };
  }, [height]);

  const handleRowClick = (event: CortexEventLogRow) => {
    setSelectedEvent((prev) => (prev?.id === event.id ? null : event));
  };

  return (
    <>
      {/* Floating trigger */}
      {!isOpen && !hideFloatingTrigger && (
        <button
          type="button"
          onClick={toggle}
          title="Open Orchestration Console (⌘`)"
          className={cn(
            "fixed bottom-4 z-[70]",
            "h-10 w-10 rounded-full",
            "bg-background/90 border border-border shadow-lg backdrop-blur-sm",
            "flex items-center justify-center text-muted-foreground hover:text-foreground",
            "transition-colors",
          )}
          style={{ right: 16 }}
        >
          <Terminal className="h-4 w-4" />
        </button>
      )}

      {/* Bottom panel */}
      <div
        aria-label="Orchestration Console"
        role="complementary"
        className={cn(
          "fixed z-[70] pointer-events-auto",
          "flex flex-col bg-background border-t border-border shadow-[0_-4px_24px_rgba(0,0,0,0.25)]",
          isOpen ? "translate-y-0 opacity-100" : "translate-y-full opacity-0 pointer-events-none",
          className,
        )}
        style={{
          height,
          bottom: 0,
          left: sidebarWidth,
          right: rightDrawerWidth,
          // Horizontal edges track layout via ResizeObserver (no CSS transition).
          // Cortex drawer shell animates width open/closed; console follows each frame.
          transition: "transform 200ms ease-in-out, opacity 150ms ease-in-out",
        }}
      >
        {/* Drag handle — visual track spans full width but only the grip icon receives pointer events so it doesn't block header buttons. */}
        <div
          className={cn(
            "absolute top-0 left-0 right-0 h-5 -translate-y-1/2 z-10",
            "flex items-center justify-center cursor-ns-resize",
            "text-muted-foreground/50 hover:text-muted-foreground transition-colors",
            "pointer-events-none",
          )}
        >
          <div
            onMouseDown={handleResizeStart}
            onTouchStart={handleResizeStart}
            className="pointer-events-auto flex items-center justify-center h-4 w-8"
          >
            <GripHorizontal className="h-4 w-4" />
          </div>
        </div>

        {/* Header — relative z-20 ensures it sits above the drag handle's z-10 so buttons always receive clicks. */}
        <div className="relative z-20 flex items-center justify-between px-3 py-2 border-b border-border/60 shrink-0">
          <div className="flex items-center gap-2">
            <Terminal className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-sm font-semibold">Orchestration Console</span>
            {isLoading && (
              <span className="text-[10px] text-muted-foreground animate-pulse">
                loading…
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <ConnectionIndicator isConnected={isConnected} />
            <DevModeToggle />
            {onExport && (
              <button
                type="button"
                onClick={() => onExport(filteredEvents)}
                title={`Export ${filteredEvents.length} events as NDJSON`}
                disabled={filteredEvents.length === 0}
                className="h-6 w-6 rounded flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-40 disabled:pointer-events-none"
              >
                <Download className="h-3.5 w-3.5" />
              </button>
            )}
            {onClear && (
              <button
                type="button"
                onClick={onClear}
                title="Clear events"
                className="h-6 w-6 rounded flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                toggle();
              }}
              onPointerDown={(e) => e.stopPropagation()}
              title="Close console"
              aria-label="Close Orchestration Console"
              data-testid="console-close-btn"
              className="relative z-30 h-6 w-6 rounded flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <ConsoleTabs
          activeTab={activeTab}
          onTabChange={setActiveTab}
          counts={counts}
        />

        {focusFilter && (
          <div className="flex items-center gap-2 px-3 py-1.5 border-b border-border/60 shrink-0 bg-muted/30">
            <span className="text-[11px] text-muted-foreground truncate">
              Focused on{" "}
              <span className="font-medium text-foreground">
                {focusFilter.label ?? focusFilter.agentId ?? "selection"}
              </span>
              {filteredEvents.length !== events.length && (
                <span className="text-muted-foreground">
                  {" "}
                  · {filteredEvents.length}/{events.length}
                </span>
              )}
            </span>
            <button
              type="button"
              onClick={() => setFocusFilter(null)}
              className="ml-auto shrink-0 text-[11px] text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
            >
              Clear
            </button>
          </div>
        )}

        {/* Body */}
        <div
          ref={listRef}
          className="flex-1 overflow-y-auto min-h-0 sexy-scrollbar"
        >
          {filteredEvents.length === 0 && !isLoading && <EmptyState />}
          {filteredEvents.map((event) => (
            <div key={event.id}>
              <EventRow
                event={event}
                onClick={handleRowClick}
                isSelected={selectedEvent?.id === event.id}
              />
              {selectedEvent?.id === event.id && (
                <div className="border-b border-border/40 bg-muted/20">
                  <EventDetail event={event} devMode={devMode} />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

// ── ConsoleToggle button ───────────────────────────────────────────────────

export interface ConsoleToggleProps {
  variant?: "ghost" | "outline" | "secondary";
  size?: "sm" | "icon" | "default";
  className?: string;
  showLabel?: boolean;
}

export function ConsoleToggle({
  variant = "ghost",
  size = "icon",
  className,
  showLabel = false,
}: ConsoleToggleProps) {
  return (
    <button
      type="button"
      onClick={() => toggleOrchestrationConsole()}
      title="Toggle Orchestration Console (⌘`)"
      aria-label="Toggle Orchestration Console"
      className={cn(
        "inline-flex items-center justify-center gap-1.5 rounded-md",
        "text-muted-foreground hover:text-foreground hover:bg-muted",
        "transition-colors disabled:opacity-50 disabled:pointer-events-none",
        size === "icon" && "h-8 w-8",
        size === "sm" && "h-8 px-2 text-xs",
        size === "default" && "h-9 px-3 text-sm",
        variant === "outline" && "border border-border bg-background hover:bg-muted",
        variant === "secondary" && "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        className,
      )}
    >
      <Terminal className="h-4 w-4" />
      {showLabel && <span className="hidden sm:inline">Console</span>}
    </button>
  );
}
